// ============================================================================
// BP-13-02: Admin Vendors Hooks
// BrightPath Platform - Admin Frontend
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminModerationApi } from '@/features/admin/api/adminApi';
import type {
  VendorVerificationListParams,
  VendorVerificationAction,
} from '@/types/admin';
import { useToast } from '@/core/hooks/useToast';
import { moderationKeys } from './useModeration';

// =============================================================================
// Query Keys
// =============================================================================

export const vendorKeys = {
  all: ['admin', 'vendors'] as const,
  verifications: () => [...vendorKeys.all, 'verifications'] as const,
  verificationList: (params: VendorVerificationListParams) =>
    [...vendorKeys.verifications(), params] as const,
  verification: (vendorId: string) => [...vendorKeys.verifications(), vendorId] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch pending vendor verifications
 */
export function useVendorVerifications(params: VendorVerificationListParams) {
  return useQuery({
    queryKey: vendorKeys.verificationList(params),
    queryFn: () => adminModerationApi.listVendorVerifications(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch a single vendor verification details
 */
export function useVendorVerification(vendorId: string) {
  return useQuery({
    queryKey: vendorKeys.verification(vendorId),
    queryFn: () => adminModerationApi.getVendorVerification(vendorId),
    enabled: !!vendorId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Take action on vendor verification
 */
export function useVendorVerificationAction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      vendorId,
      action,
    }: {
      vendorId: string;
      action: VendorVerificationAction;
    }) => adminModerationApi.verifyVendor(vendorId, action),
    onSuccess: (vendor, { action }) => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.verifications() });
      queryClient.setQueryData(vendorKeys.verification(vendor.vendorId), vendor);
      queryClient.invalidateQueries({ queryKey: moderationKeys.stats() });

      const actionMessages: Record<string, { title: string; description: string }> = {
        approve: {
          title: 'Vendor approved',
          description: `${vendor.vendorName} has been verified and can now list products.`,
        },
        reject: {
          title: 'Vendor rejected',
          description: `${vendor.vendorName}'s verification has been rejected.`,
        },
        request_info: {
          title: 'Information requested',
          description: `Additional information has been requested from ${vendor.vendorName}.`,
        },
      };

      const message = actionMessages[action.action];
      toast({
        title: message.title,
        description: message.description,
        variant: action.action === 'approve' ? 'success' : 'info',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Action failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Quick approve vendor
 */
export function useApproveVendor() {
  const verificationAction = useVendorVerificationAction();

  return {
    ...verificationAction,
    mutate: (vendorId: string, notes?: string) =>
      verificationAction.mutate({
        vendorId,
        action: { action: 'approve', notes, notifyVendor: true },
      }),
    mutateAsync: (vendorId: string, notes?: string) =>
      verificationAction.mutateAsync({
        vendorId,
        action: { action: 'approve', notes, notifyVendor: true },
      }),
  };
}

/**
 * Quick reject vendor
 */
export function useRejectVendor() {
  const verificationAction = useVendorVerificationAction();

  return {
    ...verificationAction,
    mutate: (vendorId: string, notes?: string) =>
      verificationAction.mutate({
        vendorId,
        action: { action: 'reject', notes, notifyVendor: true },
      }),
    mutateAsync: (vendorId: string, notes?: string) =>
      verificationAction.mutateAsync({
        vendorId,
        action: { action: 'reject', notes, notifyVendor: true },
      }),
  };
}

/**
 * Request additional information from vendor
 */
export function useRequestVendorInfo() {
  const verificationAction = useVendorVerificationAction();

  return {
    ...verificationAction,
    mutate: (vendorId: string, requestedDocuments: string[], notes?: string) =>
      verificationAction.mutate({
        vendorId,
        action: {
          action: 'request_info',
          requestedDocuments,
          notes,
          notifyVendor: true,
        },
      }),
    mutateAsync: (vendorId: string, requestedDocuments: string[], notes?: string) =>
      verificationAction.mutateAsync({
        vendorId,
        action: {
          action: 'request_info',
          requestedDocuments,
          notes,
          notifyVendor: true,
        },
      }),
  };
}


